import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery, PageResult } from '@/types/crud'

export interface StatisticalDeptFilters {
  channelId: string | number
  deptId: string | number
  accessTime: string[]
}

export interface StatisticalDeptRecord {
  deptId: string | number
  deptName: string
  channelName: string
  pv: number
  uv: number
  ip: number
  duration: number
}

export interface StatisticalDeptSummary {
  pv: number
  uv: number
  ip: number
}

export interface StatisticalDeptChannel {
  rowId: string | number
  eventName: string
}

function buildParams(query: PageQuery<StatisticalDeptFilters>) {
  return {
    ...query.filters,
    pageNo: query.pageNo,
    pageSize: query.pageSize
  }
}

function normalizeSummary(payload: unknown): StatisticalDeptSummary {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('汇总响应必须是对象')
  }
  const summary = payload as Partial<StatisticalDeptSummary>
  if (
    typeof summary.pv !== 'number' ||
    typeof summary.uv !== 'number' ||
    typeof summary.ip !== 'number'
  ) {
    throw new Error('汇总响应缺少有效的 pv、uv 或 ip')
  }
  return summary as StatisticalDeptSummary
}

async function requireExportFile(blob: Blob) {
  if (!blob.type.includes('application/json')) return blob
  const payload = JSON.parse(await blob.text()) as { message?: string }
  throw new Error(payload.message || '导出失败')
}

export async function fetchStatisticalDeptPage(
  query: PageQuery<StatisticalDeptFilters>,
  signal: AbortSignal
): Promise<PageResult<StatisticalDeptRecord>> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalDept/queryByCondition',
    params: buildParams(query),
    signal
  })
  return normalizePageResult<StatisticalDeptRecord>(payload)
}

export async function fetchStatisticalDeptSummary(
  filters: Partial<StatisticalDeptFilters>,
  signal: AbortSignal
): Promise<StatisticalDeptSummary> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalDept/queryStatistical',
    params: {
      ...filters,
      pageNo: 1,
      pageSize: 1000
    },
    signal
  })
  return normalizeSummary(payload)
}

export async function fetchStatisticalDeptChannels(): Promise<
  StatisticalDeptChannel[]
> {
  const payload = await requestV2.get<unknown>({
    url: '/main/customerEvents/queryByCondition',
    params: { pageNo: 1, pageSize: 1000 }
  })
  return normalizePageResult<StatisticalDeptChannel>(payload).records
}

export function exportStatisticalDept(
  query: PageQuery<StatisticalDeptFilters>
) {
  return requestV2
    .get<Blob>({
      url: '/main/liveStatisticalDept/download',
      params: buildParams(query),
      responseType: 'blob'
    })
    .then(requireExportFile)
}
